import { useEffect, useState } from "react";

import { difference } from "lodash";
import { useToasts } from "react-toast-notifications";

import Bugsnag from "@bugsnag/browser";
import { usePrettyLogEventPage } from "@components/v2/analytics/hooks/use-pretty-log-event";
import { RecipientsSelector } from "@features/data-export/components/schedule-configurator/recipients-selector";
import { FETCH_DATA_EXPORT_EMAIL_RECIPIENTS } from "@gql/data-export";
import { SHARE_DOCUMENT } from "@gql/documents";
import {
  useFeatureFlags,
  useNorthbeamMutation,
  useNorthbeamQuery,
} from "@hooks/http";
import { type User } from "@internal/user";
import {
  type Documents,
  type FetchDataExportEmailRecipients,
  type ShareDocument,
  type ShareDocumentVariables,
} from "@north-beam/nb-client-side";
import { IconButton } from "@shared/buttons";
import { Modal } from "@shared/modal";

type Doc = Documents["me"]["documents"]["data"][number];

export interface Props {
  isOpen: boolean;
  onClose: () => void;
  readonly user: User;
  readonly document: Doc;
}

export const ShareDocumentModal = ({
  user,
  document,
  isOpen,
  onClose,
}: Props) => {
  const prettyLogEventPage = usePrettyLogEventPage();

  const { addToast } = useToasts();
  const { hasFeatureFlag } = useFeatureFlags();

  const [recipients, setRecipients] = useState<string[]>([]);

  useEffect(() => {
    setRecipients([]);
  }, [isOpen]);

  const {
    data: fetchedDataExportEmailRecipients,
    loading: isLoadingInternalUsers,
  } = useNorthbeamQuery<FetchDataExportEmailRecipients>(
    FETCH_DATA_EXPORT_EMAIL_RECIPIENTS,
    { skip: !isOpen },
  );

  const { me: { dataExportEmailRecipients: internalUsers = [] } = {} } =
    fetchedDataExportEmailRecipients || {};

  const [shareDocumentMutation, { loading: isSharingDocument }] =
    useNorthbeamMutation<ShareDocument, ShareDocumentVariables>(
      SHARE_DOCUMENT,
      {
        doNotRaiseErrorOnFailure: true,
        onCompleted: () =>
          addToast("Document shared successfully!", {
            appearance: "success",
          }),
        onError: () =>
          addToast("There was an error on sharing the document!", {
            appearance: "error",
          }),
      },
    );

  const onShareDocument = async () => {
    prettyLogEventPage({
      domain: "Share Button",
      action: "onClick",
    });

    await shareDocumentMutation({
      variables: {
        documentName: document.name,
        recipients,
      },
    })
      .catch((err) =>
        Bugsnag.notify(
          `Error on sharing document [${document.name}] from user [${
            user.authedUserId
          }]: ${err.toString()}`,
        ),
      )
      .finally(() => {
        setRecipients([]);
        onClose();
      });
  };

  const recipientsListContainsExternalUsers =
    difference(recipients, internalUsers).length > 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      titleContent={<div className="font-bold text-xl">Share Document</div>}
    >
      <div className="mb-2">
        <h3 className="text-base font-semibold m-0 mb-2">Document</h3>
        <p className="text-xs text-zinc-500 font-light m-0 mb-6">
          {document.name}
        </p>
      </div>
      <div className="mb-4 w-full space-y-2 overflow-y-visible">
        <h3 className="text-base font-semibold">Select email recipient(s)</h3>
        <p className="text-xs text-zinc-500 font-light">
          They will receive an email with a link to download this file with a 7
          day expiration.
        </p>
        <RecipientsSelector
          allowCreatingOptions={hasFeatureFlag(
            "enableDataExportToExternalUsers",
          )}
          isLoading={isLoadingInternalUsers}
          initialOptions={internalUsers.map((internalUser) => ({
            label: internalUser,
            value: internalUser,
          }))}
          tags={recipients.map((recipient) => ({
            label: recipient,
            value: recipient,
          }))}
          onChange={(selectedRecipients) => {
            setRecipients(
              selectedRecipients.map((recipient) => recipient.value),
            );
          }}
        />
      </div>
      {recipientsListContainsExternalUsers && (
        <div
          className="alert alert-warning flex items-center justify-center gap-4 py-2 px-4"
          role="alert"
        >
          <i className="fa-solid fa-exclamation-triangle text-sun+2 text-xl" />
          <div className="flex flex-col">
            You are about to share this document with users from outside of your
            organization.
            <u>Are you sure you want to proceed?</u>
          </div>
        </div>
      )}
      <div className="flex items-end justify-end">
        <IconButton
          variant={recipientsListContainsExternalUsers ? "danger" : "primary"}
          leftIcon={<i className="fa-sharp fa-regular fa-link" />}
          onClick={onShareDocument}
          loading={isSharingDocument}
        >
          Share document
        </IconButton>
      </div>
    </Modal>
  );
};
